import React from 'react';
import Collapse from 'react-md/lib/Helpers/Collapse';
import FontIcon from 'react-md/lib/FontIcons/FontIcon';
import Button from 'react-md/lib/Buttons';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import some from 'lodash/some';
import unset from 'lodash/unset';
import keys from 'lodash/keys';
import XRegExp from 'xregexp';
/* eslint-disable import/first */
const lucene = require("lucene");

import redCircledBin from 'vendor/svg/feed/definition/bin_icon.svg';
import FocusFeedDefinitionSocialExpression from './Expression/FocusFeedDefinitionSocialExpression';
import SimpleDialog from 'components/common/dialog/simple';
import { withT } from 'lib/i18n';
import { ComponentProps } from './types';
import {
	ErrorProfile, ProfileExpressionsType, ScopeSocial,
	SocialExpression, SocialExpressionsType, SocialThreshold,
	anywhereScope, isHashtagScope, isMentionsScope
} from 'class/Feed';
import { checkLuceneQueryValue } from 'lib/luceneValidator';

import './FocusFeedDefinitionSocial.scss';
import FocusFeedDefinitionSocialExpressionProfile from './Expression/FocusFeedDefinitionSocialExpressionProfile';
import FocusFeedDefinitonSocialThreshold from './Threshold/FocusFeedDefinitonSocialThreshold';
import FocusFeedDefinitionMultipleExpressions from '../MultipleExpressions/FocusFeedDefinitionMultipleExpressions';
import featureFlags from 'lib/featureFlags';
import providerDiscovery from 'lib/providerDiscovery';
import ListDialog from 'components/common/dialog/list/ListDialog';
import { Columns } from 'components/common/DiscoverTable';
import FocusFeedDefinitionInformation from '../Layout/Sections/Information/';

type ComponentState = {
	expressionCollapse: boolean
	specificationCollapse: boolean
	filterInstagramFollowersCollapse: boolean
	showRemoveExpressionDialog: boolean
	showRemoveAllExpressionsDialog: boolean
	showMassiveSocialInclude: boolean
	showMassiveSocialExclude: boolean
	removeExpressionIndex: number
	removeExpressionType: SocialExpressionsType
};

const HELPJUICE_SOCIAL_HELP = "https://help.launchmetrics.com/en_US/feed-management/management-of-social-feeds";

class FocusFeedDefinitionSocial extends React.Component<ComponentProps, ComponentState> {

	constructor(props: ComponentProps) {
		super(props);
		this.state = {
			expressionCollapse: false,
			specificationCollapse: false,
			filterInstagramFollowersCollapse: true,
			showRemoveExpressionDialog: false,
			showRemoveAllExpressionsDialog: false,
			showMassiveSocialInclude: false,
			showMassiveSocialExclude: false,
			removeExpressionIndex: 0,
			removeExpressionType: 'include_expressions'
		};
	}

	private _getFixedQuery = (query: string, scope: ScopeSocial[]): string => {
		if (!isEqual(scope, anywhereScope)) query = query.replace(/["|']/g, '');
		query = query.replace(/[@#]/g, '');
		return query;
	}

	private _onCheckExpressionQuery = (expression: SocialExpression, type: SocialExpressionsType | 'main', index: number) => {
		const { onChangeQuery, onSetExpressionError } = this.props;
		const { onChangeMainQuery, onSetMainExpressionError } = this.props;

		const query: string = cloneDeep(expression.q).trim();
		const scope: ScopeSocial[] = cloneDeep(expression.scope);

		const fixedQuery = this._getFixedQuery(query, scope);
		const queryValidated = this._isValidQuery(fixedQuery, scope);
		if (type === 'main') {
			onSetMainExpressionError(!queryValidated);
			onChangeMainQuery(fixedQuery);
		} else {
			onSetExpressionError(!queryValidated, type, index);
			onChangeQuery(fixedQuery, type, index);
		}
	}

	private _isValidQuery = (query: string, scope: ScopeSocial[]): boolean => {
		if (query.trim() === "") return true;
		if (!checkLuceneQueryValue(query)) return false;
		else if ((isMentionsScope(scope) || isHashtagScope(scope))  && !query.match(/^$|(^(?:\b\S{2,}\b\s*)+$)/)) return false;
		/*
			\p{L} matches a single code point in the category "letter".
			\p{N} matches any kind of numeric character in any script.
			\p{Z} or \p{Separator}: any kind of whitespace or invisible separator
		*/
		else if (!query.match(XRegExp('^$|[\\p{L}\\p{N}"\\p{Z}]{2,}'))) return false;
		else
			try {
				// Check lucene syntax
				const results = lucene.parse(query);
				if (this._queryHasField(results) && !this._isSensitiveQuery(query)) return false;
				else return true;
			} catch (err) {
				return false;
			}
	}

	private _queryHasField = (parsedQuery: any) => {
		if (parsedQuery.field) return parsedQuery.field !== '<implicit>';
		if (parsedQuery.left && this._queryHasField(parsedQuery.left)) return true;
		if (parsedQuery.right && this._queryHasField(parsedQuery.right)) return true;
		return false;
	}

	private _isSensitiveQuery = (query: string) => {
		return query.match('sen:');
	}

	private _onMultipleProfiles(input: string, type: ProfileExpressionsType) {
		const { onSearchMultipleProfiles } = this.props;
		const splitProfileURLs = input.split('\n').map(pu => pu.toLowerCase()).filter(u => u && u !== '');
		onSearchMultipleProfiles(splitProfileURLs, type);
		type === 'include_profiles'
			? this.setState({ showMassiveSocialInclude: false })
			: this.setState({ showMassiveSocialExclude: false });
	}

	private _onChangeScope = (scope: ScopeSocial[], q: string, type: SocialExpressionsType | 'main', index: number) => {
		const { onChangeQuery, onChangeScope } = this.props;
		const { onChangeMainQuery, onChangeMainScope } = this.props;

		const query: string = q.trim();
		const fixedQuery = this._getFixedQuery(query, scope);
		if (type === 'main') {
			onChangeMainScope(scope);
			if (!isEqual(query, fixedQuery)) onChangeMainQuery(fixedQuery);
		} else {
			onChangeScope(scope, type, index);
			if (!isEqual(query, fixedQuery)) onChangeQuery(fixedQuery, type, index);
		}
	}

	private _getMainExpressionContent() {
		const { definition, instagramAccounts } = this.props;
		const { onChangeMainQuery, onToggleEnableMainQuery, onToggleInstagramAccount } = this.props;

		return (
			<div className="definition-expression-main">
				<FocusFeedDefinitionSocialExpression
					expressionKey="main"
					index={0}
					enabled={definition.main.enabled}
					query={definition.main.q}
					scope={definition.main.scope}
					error={definition.main.error}
					instagramAccounts={instagramAccounts}
					definitionInstagramAccounts={definition.instagram_accounts}
					onEnabledToggled={onToggleEnableMainQuery}
					onQueryChanged={q => onChangeMainQuery(q)}
					onCheckQuery={() => this._onCheckExpressionQuery(definition.main, 'main', 0)}
					onScopeChanged={scope => this._onChangeScope(scope, definition.main.q, 'main', 0)}
					onInstagramAccountToggled={onToggleInstagramAccount}
				/>
			</div>
		);
	}

	private _checkRemoveExpression(q: string, type: SocialExpressionsType, index: number) {
		const { onRemoveExpression, onRemoveProfileExpression } = this.props;
		if (q) return this.setState({ showRemoveExpressionDialog: true, removeExpressionIndex: index, removeExpressionType: type });
		type === 'include_profiles' || type === 'exclude_profiles' ? onRemoveProfileExpression(type, index) : onRemoveExpression(type, index);
	}

	private _onClearAllSocialProfiles(type: SocialExpressionsType) {
		this.setState({ showRemoveAllExpressionsDialog: true, removeExpressionType: type });
	}

	private _onThresholdChange(threshold: SocialThreshold, channel: number, value: number | '') {
		const { t, onSetThreshold } = this.props;
		const thresholdCopy = cloneDeep(threshold);

		if (value === '') {
			thresholdCopy.error = true;
			thresholdCopy.errorMessage = t('feed.form.threshold.invalid_value');
		} else if (channel === 30 && value < 500) {
			thresholdCopy.error = true;
			thresholdCopy.errorMessage = t('feed.form.threshold.invalid_min_value');
		} else {
			unset(thresholdCopy, 'error');
			unset(thresholdCopy, 'errorMessage');
		}

		thresholdCopy.value = value;
		onSetThreshold(channel, thresholdCopy);
	}

	private _getFilterByFollowersSection() {
		const { filterInstagramFollowersCollapse } = this.state;
		const { definition } = this.props;
		const { t, onSetThreshold } = this.props;
		const sectionDisabled = some(definition.include_profiles, element => element.enabled && element.url !== '' && !element.error);
		return (
			<div id="focusFeedDefinitionSocialThresholdSection" className="definition-section">
				<div className="definition-section-header">
					<div className="definition-header-title" onClick={() => this.setState({ filterInstagramFollowersCollapse: !filterInstagramFollowersCollapse })}>
						{
							filterInstagramFollowersCollapse ?
								<FontIcon className="definition-header-title-toggle" >keyboard_arrow_right</FontIcon> :
								<FontIcon className="definition-header-title-toggle">keyboard_arrow_down</FontIcon>
						}
						<span>
							<span className="definition-header-title-text"> {t('feed.form.filter_threshold')} </span>
							- {t('feed.form.filter_mentions.description')}
						</span>
					</div>
				</div>
				<Collapse collapsed={filterInstagramFollowersCollapse}>
					<div className="thresholds-container">
						{sectionDisabled ?
							<div className="threshold-section-warning">
								{t('feed.form.threshold.active_profiles_warning')}
							</div>
							: null}
						{keys(definition.threshold).map(channel => {
							const threshold = definition.threshold[+channel];
							return (
								<FocusFeedDefinitonSocialThreshold
									key={'threshold_filter_channel' + channel}
									channel={+channel}
									checked={threshold.enabled}
									disabled={sectionDisabled}
									value={threshold.value}
									error={threshold.error}
									errorMessage={threshold.errorMessage}
									onCheckToggled={checked => {
										const thresholdCopy = cloneDeep(threshold);
										thresholdCopy.enabled = checked;
										onSetThreshold(+channel, thresholdCopy);
									}}
									onValueChanged={value => this._onThresholdChange(threshold, +channel, value)}
								/>
							);
						})}
					</div>
				</Collapse>
			</div>
		);
	}

	private _getSourceSpecificationSection() {
		const { definition, loadingProfiles, onAddProfileExpression, onChangeQueryProfile, onSearchProfile, onToggleEnableQuery } = this.props;
		const { t } = this.props;
		return (
			<div className="definition-section">
				<div className="definition-section-header">
					<div className="definition-header-title" onClick={() => this.setState({ specificationCollapse: !this.state.specificationCollapse })}>
						{
							this.state.specificationCollapse ?
								<FontIcon className="definition-header-title-toggle" >keyboard_arrow_right</FontIcon> :
								<FontIcon className="definition-header-title-toggle">keyboard_arrow_down</FontIcon>
						}
						<span className="definition-header-title-text">{t('feed.form.header_source_expression')}</span>
					</div>
				</div>
				<Collapse collapsed={this.state.specificationCollapse}>
					<div className="definition-section-content">
						<div className="definition-expressions">
							<div className="definition-social-text-header">
								<div className="definition-text" dangerouslySetInnerHTML={{ __html: t('feed.form.header_include_profiles_react') + ":" }}></div>
								{
									definition.include_profiles.length > 0 &&
									<Button
										id="definitionSocialRemoveAllIncludeProfiles"
										className='definition-social-remove-all-button'
										flat
										onClick={() => this._onClearAllSocialProfiles('include_profiles')}
									>
										<div>{t('feed.form.header_clear_all')}</div>
										<FontIcon className='definition-social-remove-all-button-icon'>delete</FontIcon>
									</Button>
								}
							</div>
							{
								!this.state.showMassiveSocialInclude &&
								<div className="definition-add-expression">
									<Button
										id="focusFeedDefinitionSocialAddIncludeProfileBtn"
										className="definition-add-expression-button"
										flat
										onClick={() => { if(!this.state.showMassiveSocialInclude) onAddProfileExpression('include_profiles'); }}
									>
										<span>+</span> {t('feed.form.button.add_words_react')}
									</Button>
									<Button
										id="focusFeedDefinitionSocialAddIncludeManyProfilesBtn"
										className="definition-add-expression-button"
										flat
										onClick={() => { this.setState({ showMassiveSocialInclude: true }); }}
									>
										<span>+</span> {t('feed.form.button.add_url_list')}
									</Button>
								</div>
							}
							<FocusFeedDefinitionMultipleExpressions
								key="socialMultipleExpressions"
								enabled={this.state.showMassiveSocialInclude}
								placeholder={t('feed.form.multiple_urls.placeholder_enter_urls')}
								cancelLabel={t('feed.form.multiple_urls.cancel')}
								submitLabel={t('feed.form.multiple_urls.submit')}
								onCancel={() => { this.setState({ showMassiveSocialInclude: false }); }}
								onSubmit={input => this._onMultipleProfiles(input, 'include_profiles')}
							/>
							{definition.include_profiles.map((expression, index) => {
								if (providerDiscovery.isParklu(expression.channel_type_id) && !featureFlags.isEnabled('parklu')) expression = { ...expression, error: true };
								if (providerDiscovery.isTikTok(expression.channel_type_id)) expression.picture = '';
								return (
									<FocusFeedDefinitionSocialExpressionProfile
										key={`includeProfiles${index}`}
										expressionKey="include_profiles"
										index={index}
										enabled={expression.enabled}
										query={expression.url}
										error={expression.error}
										channel_type={expression.channel_type_id}
										avatar={expression.picture}
										name={expression.name}
										loading={loadingProfiles.include_profiles[index]}
										onEnabledToggled={enabled => onToggleEnableQuery(enabled, 'include_profiles', index)}
										onQueryChanged={url => onChangeQueryProfile(url, 'include_profiles', index)}
										onRemoveExpression={() => this._checkRemoveExpression(expression.url, 'include_profiles', index)}
										onSearchProfile={() => onSearchProfile(expression.url, 'include_profiles', index)}
									/>
								);
							})}

							<div className="definition-social-text-header">
								<div className="definition-text" dangerouslySetInnerHTML={{ __html: t('feed.form.header_exclude_profiles_react') + ":" }}></div>
								{
									definition.exclude_profiles.length > 0 &&
									<Button
										id="definitionSocialRemoveAllExcludeProfiles"
										className='definition-social-remove-all-button'
										flat
										onClick={() => this._onClearAllSocialProfiles('exclude_profiles')}
									>
										<div>{t('feed.form.header_clear_all')}</div>
										<FontIcon className='definition-social-remove-all-button-icon'>delete</FontIcon>
									</Button>
								}
							</div>
							{
								!this.state.showMassiveSocialExclude &&
								<div className="definition-add-expression">
									<Button
										id="focusFeedDefinitionSocialAddExcludeProfileBtn"
										className="definition-add-expression-button"
										flat
										onClick={() => { onAddProfileExpression('exclude_profiles'); }}
									>
										<span>+</span> {t('feed.form.button.add_words_react')}
									</Button>
									<Button
										id="focusFeedDefinitionSocialAddExcludeManyProfilesBtn"
										className="definition-add-expression-button"
										flat
										onClick={() => { this.setState({ showMassiveSocialExclude: true }); }}
									>
										<span>+</span> {t('feed.form.button.add_url_list')}
									</Button>
								</div>
							}
							<FocusFeedDefinitionMultipleExpressions
								key="socialMultipleExpressionsExclude"
								enabled={this.state.showMassiveSocialExclude}
								placeholder={t('feed.form.multiple_urls.placeholder_enter_urls')}
								cancelLabel={t('feed.form.multiple_urls.cancel')}
								submitLabel={t('feed.form.multiple_urls.submit')}
								onCancel={() => { this.setState({ showMassiveSocialExclude: false }); }}
								onSubmit={input => this._onMultipleProfiles(input, 'exclude_profiles')}
							/>
							{definition.exclude_profiles.map((expression, index) => {
								if (providerDiscovery.isParklu(expression.channel_type_id) && !featureFlags.isEnabled('parklu')) expression = { ...expression, error: true };
								if (providerDiscovery.isTikTok(expression.channel_type_id)) expression.picture = '';
								return (
									<FocusFeedDefinitionSocialExpressionProfile
										key={`excludeProfiles${index}`}
										expressionKey="exclude_profiles"
										index={index}
										enabled={expression.enabled}
										query={expression.url}
										error={expression.error}
										channel_type={expression.channel_type_id}
										avatar={expression.picture}
										name={expression.name}
										loading={loadingProfiles.exclude_profiles[index]}
										onEnabledToggled={enabled => onToggleEnableQuery(enabled, 'exclude_profiles', index)}
										onQueryChanged={url => onChangeQueryProfile(url, 'exclude_profiles', index)}
										onRemoveExpression={() => this._checkRemoveExpression(expression.url, 'exclude_profiles', index)}
										onSearchProfile={() => onSearchProfile(expression.url, 'exclude_profiles', index)}
									/>
								);
							})}
						</div>
					</div>
				</Collapse>
			</div>
		);
	}

	private _getContentSpecificationSection() {
		const { definition, instagramAccounts } = this.props;
		const { t, onAddExpression, onFetchSearch, onToggleInstagramAccount, onToggleEnableQuery, onChangeQuery } = this.props;

		return (
			<div id="focusFeedDefinitionSocialContentSection" className="definition-section">
				<div className="definition-section-header">
					<div className="definition-header-title" onClick={() => this.setState({ expressionCollapse: !this.state.expressionCollapse })}>
						{
							this.state.expressionCollapse ?
								<FontIcon className="definition-header-title-toggle" >keyboard_arrow_right</FontIcon> :
								<FontIcon className="definition-header-title-toggle">keyboard_arrow_down</FontIcon>
						}
						<span className="definition-header-title-text"> {t('feed.form.header_main_expression_react')} </span>
					</div>
					<a className="definition-header-search-show-help-link" href={HELPJUICE_SOCIAL_HELP} target='blank'>
						<div>{t('definition.show_help')}</div>
					</a>
					<Button id="focusFeedDefinitionSocialSearchBtn" className="definition-header-search-button" flat onClick={() => onFetchSearch()}>
						<FontIcon>search</FontIcon>
						{t('form.search.value')}
					</Button>
				</div>

				<Collapse collapsed={this.state.expressionCollapse}>
					<div className="definition-section-content">
						<div className="definition-expressions">
							{this._getMainExpressionContent()}

							<div className="definition-text" dangerouslySetInnerHTML={{ __html: t('feed.form.header_positive_expressions') + ":" }} ></div>
							{definition.include_expressions.map((expression, index) => {
								return (
									<FocusFeedDefinitionSocialExpression
										key={`includeExpression${index}`}
										expressionKey="include_expressions"
										index={index}
										enabled={expression.enabled}
										query={expression.q}
										scope={expression.scope}
										error={expression.error}
										instagramAccounts={instagramAccounts}
										definitionInstagramAccounts={definition.instagram_accounts}
										onEnabledToggled={enabled => onToggleEnableQuery(enabled, 'include_expressions', index)}
										onQueryChanged={q => onChangeQuery(q, 'include_expressions', index)}
										onCheckQuery={() => this._onCheckExpressionQuery(expression, 'include_expressions', index)}
										onScopeChanged={scope => this._onChangeScope(scope, expression.q, 'include_expressions', index)}
										onRemoveExpression={() => this._checkRemoveExpression(expression.q, 'include_expressions', index)}
										onInstagramAccountToggled={onToggleInstagramAccount}
									/>
								);
							})}

							<div className="definition-add-expression">
								<Button
									id="focusFeedDefinitionSocialAddIncludeExpressionBtn"
									className="definition-add-expression-button"
									flat
									onClick={() => { onAddExpression('include_expressions'); }}
								>
									<span>+</span> {t('feed.form.button.add_words_react')}
								</Button>
							</div>

							<div className="definition-text" dangerouslySetInnerHTML={{ __html: t('feed.form.header_negative_expressions') + ":" }} ></div>
							{definition.exclude_expressions.map((expression, index) => {
								return (
									<FocusFeedDefinitionSocialExpression
										key={`excludeExpression${index}`}
										expressionKey="exclude_expressions"
										index={index}
										enabled={expression.enabled}
										query={expression.q}
										scope={expression.scope}
										error={expression.error}
										instagramAccounts={instagramAccounts}
										definitionInstagramAccounts={definition.instagram_accounts}
										onEnabledToggled={enabled => onToggleEnableQuery(enabled, 'exclude_expressions', index)}
										onQueryChanged={q => onChangeQuery(q, 'exclude_expressions', index)}
										onCheckQuery={() => this._onCheckExpressionQuery(expression, 'exclude_expressions', index)}
										onScopeChanged={scope => this._onChangeScope(scope, expression.q, 'exclude_expressions', index)}
										onRemoveExpression={() => this._checkRemoveExpression(expression.q, 'exclude_expressions', index)}
									/>
								);
							})}
							<div className="definition-add-expression">
								<Button
									id="focusFeedDefinitionSocialAddExcludeExpressionBtn"
									className="definition-add-expression-button"
									flat
									onClick={() => { onAddExpression('exclude_expressions'); }}
								>
									<span>+</span> {t('feed.form.button.add_words_react')}
								</Button>
							</div>
						</div>
					</div>
				</Collapse>
			</div>
		);
	}

	public render() {
		const { showRemoveExpressionDialog, showRemoveAllExpressionsDialog, removeExpressionType, removeExpressionIndex } = this.state;
		const { errorProfiles, t, onRemoveExpression, onRemoveAllProfileExpressions, onClearErrorProfiles } = this.props;

		return (
			<>
				{showRemoveExpressionDialog ?
					<SimpleDialog
						id="focusFeedDefinitionRemoveExpressionWarning"
						content={<></>}
						text={t('feed.form.confirm_remove_expression')}
						onAccept={() => { onRemoveExpression(removeExpressionType, removeExpressionIndex); this.setState({ showRemoveExpressionDialog: false }); }}
						onCancel={() => this.setState({ showRemoveExpressionDialog: false })}
						acceptColor="red"
					/>
					: null}
				{showRemoveAllExpressionsDialog ?
					<SimpleDialog
						id="focusFeedDefinitionRemoveAllExpressionWarning"
						content={<></>}
						text={t('feed.form.confirm_remove_all_expressions')}
						headerImage={redCircledBin}
						onAccept={() => { onRemoveAllProfileExpressions(removeExpressionType); this.setState({ showRemoveAllExpressionsDialog: false }); }}
						onCancel={() => this.setState({ showRemoveAllExpressionsDialog: false })}
						acceptColor="red"
					/>
					: null}
				{errorProfiles.length ? (
					<ListDialog
						id="socialErrorProfilesListDialog"
						title={t("definition.online.media.error.title")}
						headerText={t("definition.online.media.error.header")}
						tableData={{
							data: errorProfiles,
							columns: [{
								key: 'url',
								type: 'text',
								displayName: t('instagram.hashtags.form.profile.label')
							} as Columns<ErrorProfile, ErrorProfile>, {
								key: 'error',
								type: 'text',
								displayName: t('definition.online.media.error.reason')
							} as Columns<ErrorProfile, ErrorProfile>]
						}}
						onCancel={onClearErrorProfiles}
						onFillClipboard={() => (errorProfiles.map(profileError => profileError.url)).join('\n')}
					/>
				) : null}
				<div id="focusFeedSocialDefinition">
					{/* TODO provide `showFeedLinkedBrands=true` when this feature is enabled for non online feeds */}
					<FocusFeedDefinitionInformation />
					{this._getContentSpecificationSection()}
					{this._getSourceSpecificationSection()}
					{this._getFilterByFollowersSection()}
				</div>
			</>
		);
	}
}

export default withT(FocusFeedDefinitionSocial);
